import { AlertTriangle, BarChart3, DollarSign, Loader2, MessageSquare, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatCost, formatTokens } from "@/lib/formatters";
import { usageService } from "@/lib/services/admin";
import type { MonthlySpend, MySpendByModel, MySpendResponse } from "@/types";

const FEATURE_LABELS: Record<string, string> = {
  chat: "Chat",
  chat_title: "Chat titles",
  agent_routing: "Agent routing",
  agent: "Agents",
  group_chat: "Brainstorm",
  api: "API",
  task: "Autopilot",
  task_planning: "Autopilot (Planning)",
  flow: "Flows",
  deep_research: "Deep Research",
};

interface CostByModelChartProps {
  byModel: MySpendByModel[];
}

function CostByModelChart({ byModel }: Readonly<CostByModelChartProps>) {
  const entries = byModel.filter((m) => m.costUsd >= 0.001).sort((a, b) => b.costUsd - a.costUsd);
  const maxCost = entries.length > 0 ? entries[0].costUsd : 0.01;

  return (
    <div className="bg-surface-secondary border border-border-secondary rounded-lg p-4">
      <h3 className="text-sm font-semibold text-content-primary mb-3">Cost by Model</h3>
      <div className="space-y-2">
        {entries.map((model) => (
          <div key={model.modelId} className="flex items-center gap-3">
            <div className="w-32 text-xs text-content-secondary truncate" title={model.modelId}>
              {model.modelId}
            </div>
            <div className="flex-1 h-5 bg-surface-primary rounded overflow-hidden">
              <div
                className="h-full bg-brand-gradient rounded"
                style={{ width: `${Math.max(4, (model.costUsd / maxCost) * 100)}%` }}
              />
            </div>
            <div className="w-16 text-xs text-content-secondary text-right">{formatCost(model.costUsd)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface MonthlyTrendChartProps {
  monthlyTrend: MonthlySpend[];
}

function MonthlyTrendChart({ monthlyTrend }: Readonly<MonthlyTrendChartProps>) {
  const trendMap = new Map(monthlyTrend.map((m) => [m.month, m]));
  const allMonths: { month: string; costUsd: number; requests: number }[] = [];
  const sorted = [...monthlyTrend].sort((a, b) => a.month.localeCompare(b.month));
  const start = sorted[0].month;
  const end = sorted.at(-1)!.month;
  let [y, m] = start.split("-").map(Number);
  const [ey, em] = end.split("-").map(Number);
  while (y < ey || (y === ey && m <= em)) {
    const key = `${y}-${String(m).padStart(2, "0")}`;
    allMonths.push(trendMap.get(key) ?? { month: key, costUsd: 0, requests: 0 });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }

  const maxCost = Math.max(...allMonths.map((m) => m.costUsd), 0.01);
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <div className="bg-surface-secondary border border-border-secondary rounded-lg p-4">
      <h3 className="text-sm font-semibold text-content-primary mb-3 flex items-center gap-2">
        <BarChart3 className="h-4 w-4" />
        Monthly Trend
      </h3>
      <div className="flex items-end gap-2 h-32">
        {allMonths.map((month) => {
          const monthNum = Number.parseInt(month.month.slice(5), 10);
          const label = monthNames[monthNum - 1];
          const heightPct = month.costUsd > 0 ? Math.max(4, (month.costUsd / maxCost) * 100) : 0;
          return (
            <div key={month.month} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="w-full flex items-end justify-center" style={{ height: "5.5rem" }}>
                {heightPct > 0 ? (
                  <div
                    className="w-full max-w-10 bg-brand-gradient rounded-t"
                    style={{ height: `${heightPct}%` }}
                    title={`${label}: ${formatCost(month.costUsd)} (${month.requests} requests)`}
                  />
                ) : (
                  <div
                    className="w-full max-w-10 bg-surface-primary rounded-t"
                    style={{ height: "2px" }}
                    title={`${label}: $0.00`}
                  />
                )}
              </div>
              <div className="text-center">
                <span className="text-[10px] text-content-quaternary">{label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BudgetCard() {
  const { profile } = useAuth();
  const budget = profile?.budget;

  if (!budget) return null;

  const isBlocked = budget.percentUsed >= 100;
  const isWarning = budget.percentUsed >= 80;
  const resetsDate = new Date(budget.resetsAt).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
  });

  let barColor: string;
  let borderColor: string;
  if (isBlocked) {
    barColor = "bg-red-500";
    borderColor = "border-red-500/30";
  } else if (isWarning) {
    barColor = "bg-yellow-400";
    borderColor = "border-yellow-500/30";
  } else {
    barColor = "bg-brand-gradient";
    borderColor = "border-border-secondary";
  }

  return (
    <div className={`bg-surface-secondary border ${borderColor} rounded-lg p-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {(isBlocked || isWarning) && (
            <AlertTriangle className={`w-4 h-4 ${isBlocked ? "text-red-400" : "text-yellow-400"}`} />
          )}
          <h3 className="text-sm font-semibold text-content-primary">Monthly Budget</h3>
        </div>
        <span className="text-xs text-content-tertiary">Resets {resetsDate}</span>
      </div>
      <div className="flex items-end gap-3 mb-2">
        <span className="text-2xl font-bold text-content-primary">{formatCost(budget.currentSpendUsd)}</span>
        <span className="text-sm text-content-tertiary mb-0.5">/ {formatCost(budget.monthlyLimitUsd)}</span>
      </div>
      <div className="h-2 bg-surface-primary rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all`}
          style={{ width: `${Math.min(budget.percentUsed, 100)}%` }}
        />
      </div>
      {isBlocked && (
        <p className="text-xs text-red-400 mt-2">
          You have reached your monthly AI budget. New AI requests will resume {resetsDate}.
        </p>
      )}
      <p className="text-xs text-content-quaternary mt-2">
        Your admin has set a monthly AI budget of {formatCost(budget.monthlyLimitUsd)}.
      </p>
    </div>
  );
}

export function SettingsUsageTab() {
  const [data, setData] = useState<MySpendResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await usageService.getMySpend(6);
        setData(res.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load spend data");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-content-tertiary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12 text-content-tertiary">
        <p>{error || "No data available"}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BudgetCard />
      <div>
        <h2 className="text-base font-semibold text-content-primary">AI Usage</h2>
        <p className="text-sm text-content-tertiary mt-1">
          Your AI usage and estimated costs since{" "}
          {new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
          })}
          .
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-surface-secondary border border-border-secondary rounded-lg p-4">
          <div className="flex items-center gap-2 text-content-tertiary mb-1">
            <DollarSign className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wider">
              {new Date().toLocaleDateString(undefined, { month: "short" })} Cost
            </span>
          </div>
          <div className="text-2xl font-bold text-content-primary">{formatCost(data.currentMonthCostUsd)}</div>
        </div>
        <div className="bg-surface-secondary border border-border-secondary rounded-lg p-4">
          <div className="flex items-center gap-2 text-content-tertiary mb-1">
            <MessageSquare className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Requests</span>
          </div>
          <div className="text-2xl font-bold text-content-primary">{data.currentMonthRequests.toLocaleString()}</div>
        </div>
        <div className="bg-surface-secondary border border-border-secondary rounded-lg p-4">
          <div className="flex items-center gap-2 text-content-tertiary mb-1">
            <Zap className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Tokens In</span>
          </div>
          <div className="text-2xl font-bold text-content-primary">{formatTokens(data.currentMonthTokensInput)}</div>
        </div>
        <div className="bg-surface-secondary border border-border-secondary rounded-lg p-4">
          <div className="flex items-center gap-2 text-content-tertiary mb-1">
            <Zap className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Tokens Out</span>
          </div>
          <div className="text-2xl font-bold text-content-primary">{formatTokens(data.currentMonthTokensOutput)}</div>
        </div>
      </div>

      {/* Cost by Model */}
      {data.byModel.length > 0 && <CostByModelChart byModel={data.byModel} />}

      {/* Cost by Feature */}
      {data.byFeature.length > 0 && (
        <div className="bg-surface-secondary border border-border-secondary rounded-lg p-4">
          <h3 className="text-sm font-semibold text-content-primary mb-3">Cost by Feature</h3>
          <div className="space-y-1.5">
            {data.byFeature.map((f) => (
              <div key={f.feature} className="flex items-center justify-between text-sm">
                <span className="text-content-secondary">{FEATURE_LABELS[f.feature] || f.feature}</span>
                <div className="flex items-center gap-4">
                  <span className="text-content-quaternary text-xs">{f.requests} requests</span>
                  <span className="text-content-primary font-medium w-16 text-right">{formatCost(f.costUsd)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Trend */}
      {data.monthlyTrend.length > 1 && <MonthlyTrendChart monthlyTrend={data.monthlyTrend} />}

      <p className="text-xs text-content-quaternary">
        Costs are estimated based on token usage and model pricing. Actual billing may vary.
      </p>
    </div>
  );
}
